import type { NeoInsightLatest } from 'src/hooks/useNeoInsights'

import { AUDIT_ECONOMICS } from './data'

/**
 * Invoice Audit Agent — chat-hero insight for the finance chair of Cascade 3PL.
 *
 * PROSPECTIVE by design (verify pass #4): this is a first-touch, pre-deployment
 * wedge — Neo has NOT audited Cascade's invoices yet. So the hero frames the
 * *expected* leakage at Cascade's spend (a benchmark), not a completed audit.
 * The hard numbers ($260K found / $198K claimable / expiring tranche) are the
 * Act-2 reveal (`recoveryAtScale`) — keeping them out of the hero preserves the
 * cold-start premise and the narrow→wide arc. Benchmark numbers come from
 * `data.ts` (`AUDIT_ECONOMICS`).
 */
export const invoiceAuditInsight: NeoInsightLatest = {
  id: 'demo-insight-invoice-audit-2026-06-16',
  type: 'DAILY',
  status: 'ACTIVE',
  userEmail: null,
  summaryText:
    'Parcel invoices are dense, and they rarely match the contract line for line — duplicate accessorials, misapplied surcharges, and service-level mismatches slip through every month. At Cascade’s **~$6M/month** in carrier spend, that typically means **~1.4% leaking — on the order of ~$1M a year** — and most of it is disputable only inside the carriers’ filing windows. Nobody has time to audit thousands of line items by hand. Drop in a carrier invoice and the contracted rate card to see the exact dollars on your own data.',
  generatedAt: new Date().toISOString(),
  startDate: null,
  endDate: null,
  insights: {
    recommendations: [
      {
        id: 'invoice-audit-rec-headline',
        title:
          'Carrier billing typically leaks ~1.4% — roughly ~$1M/yr at your spend',
        description:
          'Parcel-audit firms have built whole businesses on one input: the carrier invoice versus the contracted rate card. Duplicate address-correction fees, residential surcharges applied to commercial destinations, and service-level mismatches are the usual buckets. At Cascade’s scale the recoverable amount is material — but the dispute windows close on a rolling basis, so what isn’t caught each month is lost. Upload one invoice and the rate card to turn the estimate into an exact, disputable number.',
        priority: 'high',
        category: 'cost',
        dataPoints: [
          `Monthly carrier spend ~$${(AUDIT_ECONOMICS.monthlyCarrierSpend / 1_000_000).toFixed(1)}M across FedEx, UPS, USPS, OnTrac`,
          `Typical parcel-billing leakage ~${AUDIT_ECONOMICS.leakagePct}% of spend → ~$1.0M/yr at this volume`,
          'Recovery is time-boxed — carrier dispute windows close monthly',
          'Duplicate address-correction fees are usually the single largest bucket',
        ],
        actionableSteps: [
          'Upload a recent carrier invoice + the contracted rate card to see the exact recoverable dollars',
          'Run the audit across the last 90 days and all four carriers to size the full opportunity',
          'Stand up a weekly invoice-audit agent per carrier so recovery becomes a standing line item',
        ],
        estimatedImpact:
          '~$1.0M/year recoverable or prevented once every carrier invoice is audited on arrival and the root-cause feeds are fixed.',
      },
    ],
  },
}
